import styles from "./About.module.css";
import { useState, useEffect } from "react";
import { toCamelCase } from "../../Hooks/utility";
import { usePreviewStores } from "../../data/CmsPreviewContext";
import useDetermineAndCleanJSXOutput from "../../Hooks/useDetermineAndCleanJSXOutput";
import useShortCodes from "../../Hooks/useShortCodes";
import useItemIsUsed from "../../Hooks/useItemIsUsed";
import SectionHeroTitle from "../SectionHeroTitle/SectionHeroTitle";
import { pickContentByType } from "../../data/contentPicker";

const About = () => {
  const { content } = usePreviewStores();
  const [aboutData, setAboutData] = useState({});
  const shortCodes = useShortCodes();
  const itemIsUsed = useItemIsUsed();
  // Skip empty Quill shells; keep real copy and intentional spacing.
  const textOne = itemIsUsed(aboutData.text)
    ? shortCodes({ text: aboutData.text, styles })
    : null;
  const textTwo = itemIsUsed(aboutData.textTwo)
    ? shortCodes({ text: aboutData.textTwo, styles })
    : null;
  const textThree = itemIsUsed(aboutData.textThree)
    ? shortCodes({ text: aboutData.textThree, styles })
    : null;
  const textFour = itemIsUsed(aboutData.textFour)
    ? shortCodes({ text: aboutData.textFour, styles })
    : null;
  const determineAndCleanJSXOutput = useDetermineAndCleanJSXOutput();

  useEffect(() => {
    setAboutData(pickContentByType(content, "about") || {});
  }, [content]);

  // Assemble user-supplied image css
  const imageCSSNamesArray = [
    "imageOneCSS",
    "imageTwoCSS",
    "imageThreeCSS",
    "imageFourCSS",
  ];
  const imageCSS = [];

  if (aboutData) {
    for (const imageCSSName of imageCSSNamesArray) {
      if (Object.hasOwn(aboutData, imageCSSName)) {
        imageCSS[imageCSSName] = {};
        const imageCSSNameValueArray = aboutData[imageCSSName]
          .replaceAll('"', "")
          .replaceAll("\n", "")
          .split("|");

        imageCSSNameValueArray.forEach((nameAndValue) => {
          if (!nameAndValue) return;
          const name = toCamelCase(
            nameAndValue.split(":")[0].replaceAll(" ", ""),
          );

          let value =
            nameAndValue && nameAndValue.includes(":")
              ? nameAndValue.split(":")[1].trim()
              : "";
          imageCSS[imageCSSName][name] = value;
        });
      }
    }
  }
  ///////

  return (
    <div className={styles["about-container"]}>
      <div className={styles["about-text-container"]}>
        <a
          name="about"
          href="#about"
          className={styles["anchor-page-bookmark"]}
        >
          About page bookmark
        </a>
        {/* Step 1: shared hero title with hardcoded preview image */}
        <div className={styles["section-hero-wrap"]}>
          <SectionHeroTitle
            title={aboutData.title || "About"}
            headingLevel={3}
            imageOpacity={0.125}
            imageOpacityMobile={0.25}
            imageOpacityUnderside={0.75} // lower plane (defaults to imageOpacity if omitted)
            imageOpacityUndersideMobile={0.33}
            showImageUnderside
          />
        </div>
        {textOne && (
          <p
            className={
              styles["text"] +
              " " +
              styles["text-one"] +
              " " +
              styles["content"]
            }
          >
            {determineAndCleanJSXOutput(textOne)}
          </p>
        )}
        <div className={styles["content-container"]}>
          {(aboutData.imageOneURL || textTwo) && (
            <div
              className={
                styles["text-pic-container"] +
                " " +
                styles["text-pic-container-two"]
              }
            >
              {aboutData.imageTwoURL && (
                <div className={styles["image-wrap"]}>
                  <figure>
                    <img
                      src={aboutData.imageTwoURL}
                      className={
                        styles["image"] +
                        " " +
                        styles["image-two"] +
                        " " +
                        styles["content"]
                      }
                      alt={aboutData.title + " second image "}
                      style={{ ...imageCSS.imageTwoCSS }}
                    />
                  </figure>
                </div>
              )}{" "}
              {textTwo && (
                <p
                  className={
                    styles["text"] +
                    " " +
                    styles["text-two"] +
                    " " +
                    styles["content"]
                  }
                >
                  {determineAndCleanJSXOutput(textTwo)}
                </p>
              )}{" "}
            </div>
          )}
          {(aboutData.imageThreeURL || textThree) && (
            <div
              className={
                styles["text-pic-container"] +
                " " +
                styles["text-pic-container-three"]
              }
            >
              {textThree && (
                <p
                  className={
                    styles["text"] +
                    " " +
                    styles["text-three"] +
                    " " +
                    styles["content"]
                  }
                >
                  {determineAndCleanJSXOutput(textThree)}
                </p>
              )}
              {aboutData.imageThreeURL && (
                <div className={styles["image-wrap"]}>
                  <figure>
                    <img
                      src={aboutData.imageThreeURL}
                      className={
                        styles["image"] +
                        " " +
                        styles["image-three"] +
                        " " +
                        styles["content"]
                      }
                      alt={aboutData.title + " third image "}
                      style={{ ...imageCSS.imageThreeCSS }}
                    />
                  </figure>
                </div>
              )}
            </div>
          )}
          {(aboutData.imageFourURL || textFour) && (
            <div
              className={
                styles["text-pic-container"] +
                " " +
                styles["text-pic-container-four"]
              }
            >
              {" "}
              {textFour && (
                <p
                  className={
                    styles["text"] +
                    " " +
                    styles["text-four"] +
                    " " +
                    styles["content"]
                  }
                >
                  {determineAndCleanJSXOutput(textFour)}
                </p>
              )}
              {aboutData.imageFourURL && (
                <div className={styles["image-wrap"]}>
                  <figure>
                    <img
                      src={aboutData.imageFourURL}
                      className={
                        styles["image"] +
                        " " +
                        styles["image-four"] +
                        " " +
                        styles["content"]
                      }
                      alt={aboutData.title + " fourth image "}
                      style={{ ...imageCSS.imageFourCSS }}
                    />
                  </figure>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default About;
